import {
  forwardRef,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import {
  canvasToPngBlob,
  fileToFittedCanvas,
} from '@/lib/pdf/signatureRaster';

export interface ImageSignaturePadHandle {
  isEmpty: () => boolean;
  toPngBlob: () => Promise<Blob>;
}

interface ImageSignaturePadProps {
  onDirtyChange?: (dirty: boolean) => void;
}

const MAX_BYTES = 5 * 1024 * 1024;

const ImageSignaturePad = forwardRef<ImageSignaturePadHandle, ImageSignaturePadProps>(
  function ImageSignaturePad({ onDirtyChange }, ref) {
    const canvasRef = useRef<HTMLCanvasElement | null>(null);
    const [previewUrl, setPreviewUrl] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);

    useImperativeHandle(ref, () => ({
      isEmpty: () => !canvasRef.current,
      toPngBlob: async () => {
        if (!canvasRef.current) throw new Error('Choose an image first');
        return canvasToPngBlob(canvasRef.current);
      },
    }));

    const loadFile = async (file: File | null) => {
      setError(null);
      if (!file) return;
      if (!file.type.startsWith('image/')) {
        setError('Please choose an image file');
        return;
      }
      if (file.size > MAX_BYTES) {
        setError('Image must be 5 MB or smaller');
        return;
      }
      try {
        const canvas = await fileToFittedCanvas(file);
        canvasRef.current = canvas;
        const url = canvas.toDataURL('image/png');
        setPreviewUrl(url);
        onDirtyChange?.(true);
      } catch {
        setError('Failed to load image');
        canvasRef.current = null;
        setPreviewUrl(null);
        onDirtyChange?.(false);
      }
    };

    return (
      <div className="pdfe-sig-image">
        <label
          className="pdfe-sig-dropzone"
          onDragOver={(e) => {
            e.preventDefault();
            e.stopPropagation();
          }}
          onDrop={(e) => {
            e.preventDefault();
            e.stopPropagation();
            void loadFile(e.dataTransfer.files?.[0] ?? null);
          }}
        >
          <input
            type="file"
            accept="image/*"
            hidden
            onChange={(e) => {
              void loadFile(e.target.files?.[0] ?? null);
              e.target.value = '';
            }}
          />
          {previewUrl ? (
            <img src={previewUrl} alt="Signature preview" />
          ) : (
            <span>Click or drop an image to use as your signature</span>
          )}
        </label>
        {error && <p className="pdfe-sig-error">{error}</p>}
      </div>
    );
  }
);

export default ImageSignaturePad;
